import React, { useState, useRef, useEffect, ReactNode } from 'react';
import { LayoutDashboard, Tractor, FileText, Clock } from 'lucide-react';
import './FocusPanel.css';

export interface NavItem {
  /** Unique identifier for the nav item */
  id: string;
  /** Display name */
  name: string;
  /** Icon component */
  icon: ReactNode;
  /** Optional href for link behavior */
  href?: string;
}

export interface FocusPanelProps {
  /** Currently active section ID */
  activeSection: string;
  /** Handler for section change */
  onSectionChange: (sectionId: string) => void;
  /** Custom navigation items (defaults to Workspace, My Farm, Documents, History) */
  navItems?: NavItem[];
  /** Whether the panel starts expanded */
  defaultExpanded?: boolean;
  /** Optional class name */
  className?: string;
}

/** Default navigation items for the application */
export const defaultNavItems: NavItem[] = [
  { id: 'workspace', name: 'Workspace', icon: <LayoutDashboard className="focus-panel__icon" /> },
  { id: 'my-farm', name: 'My Farm', icon: <Tractor className="focus-panel__icon" /> },
  { id: 'documents', name: 'Documents', icon: <FileText className="focus-panel__icon" /> },
  { id: 'history', name: 'History', icon: <Clock className="focus-panel__icon" /> },
];

/**
 * Focus navigation panel for application-level section switching.
 * Collapses on click outside, expands on click inside.
 */
export const FocusPanel: React.FC<FocusPanelProps> = ({
  activeSection,
  onSectionChange,
  navItems = defaultNavItems,
  defaultExpanded = true,
  className = '',
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const panelRef = useRef<HTMLDivElement>(null);

  // Handle click outside to collapse panel
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isExpanded &&
        panelRef.current &&
        !panelRef.current.contains(event.target as Node)
      ) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded]);

  // Handle clicks inside panel to expand if collapsed
  const handlePanelClick = () => {
    if (!isExpanded) {
      setIsExpanded(true);
    }
  };

  // Handle navigation item click
  const handleNavItemClick = (sectionId: string) => {
    onSectionChange(sectionId);
    // Keep panel expanded when item is clicked
  };

  return (
    <nav
      ref={panelRef}
      className={`focus-panel ${isExpanded ? 'focus-panel--expanded' : 'focus-panel--collapsed'} ${className}`}
      onClick={handlePanelClick}
      aria-label="Application navigation"
    >
      <div className="focus-panel__content">
        {navItems.map((item) => (
          <button
            key={item.id}
            className={`focus-panel__item ${activeSection === item.id ? 'focus-panel__item--active' : ''}`}
            onClick={() => handleNavItemClick(item.id)}
            aria-current={activeSection === item.id ? 'page' : undefined}
            title={!isExpanded ? item.name : undefined}
          >
            <span className="focus-panel__item-icon">{item.icon}</span>
            {isExpanded && (
              <span className="focus-panel__item-label">{item.name}</span>
            )}
          </button>
        ))}
      </div>
    </nav>
  );
};

export default FocusPanel;
